import { OrderProduct } from '../types/order';
import { normalizeServiceTypeToken } from './staffOrderCatalogFilter';

const PRIMARY_ADDRESS_SERVICE_CODES = new Set(['nbn', 'supa_networks', 'mobile', 'voice']);

function serviceTokensFromProduct(
  product: Pick<OrderProduct, 'service_type_code' | 'subcategory_id'>,
  subcategorySlugById: Record<string, string>,
): Set<string> {
  const out = new Set<string>();
  const st = normalizeServiceTypeToken(product.service_type_code);
  if (st) out.add(st);
  const slug = product.subcategory_id ? subcategorySlugById[product.subcategory_id] : '';
  const sn = normalizeServiceTypeToken(slug);
  if (sn) out.add(sn);
  return out;
}

/** Per-unit service address in wizard for these CRM / catalog shapes (incl. Voice PBX via slug or code). */
export function orderProductNeedsInstanceServiceAddress(
  product: Pick<OrderProduct, 'service_type_code' | 'subcategory_id'>,
  subcategorySlugById: Record<string, string> = {},
): boolean {
  for (const t of Array.from(serviceTokensFromProduct(product, subcategorySlugById))) {
    if (!t) continue;
    if (PRIMARY_ADDRESS_SERVICE_CODES.has(t)) return true;
    if (t.startsWith('voice')) return true;
  }
  return false;
}

export function cartHasLineServiceAddressRequirement(
  cart: { product: OrderProduct; instances: Record<string, unknown>[]; skipAddressValidation?: boolean }[],
  subcategorySlugById: Record<string, string> = {},
): boolean {
  return cart.some((row) => !row.skipAddressValidation && orderProductNeedsInstanceServiceAddress(row.product, subcategorySlugById));
}

/** Unique non-empty per-unit addresses for service types that require them; joined for the order header field. */
export function deriveOrderLevelServiceAddressFromCart(
  cart: { product: OrderProduct; instances: Record<string, unknown>[]; skipAddressValidation?: boolean }[],
  fallback: string,
  subcategorySlugById: Record<string, string> = {},
): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const row of cart) {
    if (row.skipAddressValidation) continue;
    if (!orderProductNeedsInstanceServiceAddress(row.product, subcategorySlugById)) continue;
    for (const inst of row.instances) {
      const raw = inst?.service_address;
      const s = typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim();
      if (s.length >= 3 && !seen.has(s)) {
        seen.add(s);
        parts.push(s);
      }
    }
  }
  if (parts.length) return parts.join('; ');
  return (fallback || '').trim();
}

export function validateCartInstanceServiceAddresses(
  cart: { product: OrderProduct; instances: Record<string, unknown>[]; skipAddressValidation?: boolean }[],
  subcategorySlugById: Record<string, string> = {},
): string | null {
  for (const row of cart) {
    if (row.skipAddressValidation) continue;
    if (!orderProductNeedsInstanceServiceAddress(row.product, subcategorySlugById)) continue;
    const n = row.instances.length;
    for (let i = 0; i < n; i += 1) {
      const raw = row.instances[i]?.service_address;
      const s = typeof raw === 'string' ? raw.trim() : String(raw ?? '').trim();
      if (s.length < 3) {
        return `Enter a service address for "${row.product.name}" (unit ${i + 1} of ${n}) in the product configuration step.`;
      }
    }
  }
  return null;
}
